// const API_BASE_URL = '/api/production';
const API_BASE_URL = 'https://bluerock.threedma.com/api/production';

export const productionService = {
    // Helper function to get period key for a date
    getPeriodKey: (date, groupBy = 'month') => {
        const d = new Date(date);
        switch(groupBy) {
            case 'day':
                return date.split('T')[0]; // YYYY-MM-DD
            case 'week':
                // Get Monday of the week
                const monday = new Date(d);
                monday.setDate(d.getDate() - d.getDay() + 1);
                return monday.toISOString().split('T')[0];
            case 'month':
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            case 'quarter':
                const quarter = Math.floor(d.getMonth() / 3) + 1;
                return `${d.getFullYear()}-Q${quarter}`;
            case 'year':
                return d.getFullYear().toString();
            default:
                return date.split('T')[0];
        }
    },

    // Helper function to group raw data by period
    groupByPeriod: (rawData, groupBy = 'month') => {
        if (!rawData || !rawData.length) return [];

        return rawData.reduce((acc, item) => {
            const key = productionService.getPeriodKey(item.date, groupBy);
            if (!acc[key]) {
                acc[key] = {
                    period: key,
                    records: []
                };
            }
            acc[key].records.push(item);
            return acc;
        }, {});
    },

    // Aggregate ore production data
    aggregateOreProduction: (rawData, groupBy = 'month') => {
        const grouped = productionService.groupByPeriod(rawData, groupBy);

        return Object.values(grouped).map(group => {
            // Track metrics by source
            const sourceMetrics = group.records.reduce((acc, record) => {
                const sourceName = record.source_name || 'Unknown';
                const tonnage = Number(record.tonnage) || 0;
                const grade = Number(record.grade) || 0;

                if (!acc[sourceName]) {
                    acc[sourceName] = {
                        tonnage: 0,
                        grade_sum: 0,
                        weight: 0
                    };
                }
                acc[sourceName].tonnage += tonnage;
                acc[sourceName].grade_sum += grade * tonnage;
                acc[sourceName].weight += tonnage;

                return acc;
            }, {});

            // Calculate source-specific metrics
            const sourceStats = Object.entries(sourceMetrics).map(([name, data]) => ({
                source_name: name,
                total_tonnage: data.tonnage,
                avg_grade: data.weight ? data.grade_sum / data.weight : 0
            }));

            return {
                period: group.period,
                sources: sourceStats,
                record_count: group.records.length
            };
        }).sort((a, b) => a.period.localeCompare(b.period));
    },

    // Aggregate milling data
    aggregateMilling: (rawData, groupBy = 'month') => {
        const grouped = productionService.groupByPeriod(rawData, groupBy);

        return Object.values(grouped).map(group => {
            // Track metrics by mill
            const millMetrics = group.records.reduce((acc, record) => {
                const millName = record.mill_name || 'Unknown';
                const tonnage = Number(record.tonnage) || 0;
                const runtime = Number(record.runtime) || 0;

                if (!acc[millName]) {
                    acc[millName] = {
                        tonnage: 0,
                        runtime: 0,
                        count: 0
                    };
                }

                acc[millName].tonnage += tonnage;
                acc[millName].runtime += runtime;
                acc[millName].count++;

                return acc;
            }, {});

            // Calculate mill-specific metrics
            const millStats = Object.entries(millMetrics).map(([name, data]) => ({
                mill_name: name,
                total_tonnage: data.tonnage,
                avg_runtime: data.count ? data.runtime / data.count : 0
            }));

            return {
                period: group.period,
                mills: millStats,
                record_count: group.records.length
            };
        }).sort((a, b) => a.period.localeCompare(b.period));
    },

    // Aggregate processing data
    aggregateProcessing: (rawData, groupBy = 'month') => {
        const grouped = productionService.groupByPeriod(rawData, groupBy);

        return Object.values(grouped).map(group => {
            const metrics = group.records.reduce((acc, record) => {
                const feed_tonnage = Number(record.feed_tonnage) || 0;
                const feed_grade = Number(record.feed_grade) || 0;
                const recovery = Number(record.recovery) || 0; // Stored as decimal (0-1)
                const produced_gold = Number(record.produced_gold) || 0;
                const tails_grade = Number(record.tails_grade) || 0;

                return {
                    total_feed_tonnage: acc.total_feed_tonnage + feed_tonnage,
                    feed_grade_sum: acc.feed_grade_sum + (feed_grade * feed_tonnage),
                    recovery_sum: acc.recovery_sum + (recovery * feed_tonnage), // Weighted recovery
                    total_produced_gold: acc.total_produced_gold + produced_gold,
                    tails_grade_sum: acc.tails_grade_sum + (tails_grade * feed_tonnage),
                    total_weight: acc.total_weight + feed_tonnage
                };
            }, {
                total_feed_tonnage: 0,
                feed_grade_sum: 0,
                recovery_sum: 0,
                total_produced_gold: 0,
                tails_grade_sum: 0,
                total_weight: 0
            });

            return {
                period: group.period,
                feed: {
                    tonnage: metrics.total_feed_tonnage,
                    grade: metrics.total_weight ? metrics.feed_grade_sum / metrics.total_weight : 0
                },
                recovery: metrics.total_weight ? (metrics.recovery_sum / metrics.total_weight) * 100 : 0, // Convert to percentage
                tails_grade: metrics.total_weight ? metrics.tails_grade_sum / metrics.total_weight : 0,
                produced_gold: metrics.total_produced_gold,
                record_count: group.records.length
            };
        }).sort((a, b) => a.period.localeCompare(b.period));
    },

    // Get ore production statistics
    getOreProduction: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                startDate: params.startDate || new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
                endDate: params.endDate || new Date().toISOString().split('T')[0]
            }).toString();

            const response = await fetch(`${API_BASE_URL}/ore_production.php?${queryParams}`);
            if (!response.ok) {
                throw new Error('Failed to fetch ore production data');
            }
            const result = await response.json();
            
            if (!result.success) {
                throw new Error(result.error || 'Failed to fetch ore production data');
            }

            return {
                monthlyProduction: productionService.aggregateOreProduction(result.data, params.groupBy),
                rawData: result.data
            };
        } catch (error) {
            console.error('Error fetching ore production:', error);
            throw error;
        }
    },

    // Get processing statistics
    getProcessing: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                startDate: params.startDate || new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
                endDate: params.endDate || new Date().toISOString().split('T')[0]
            }).toString();

            const response = await fetch(`${API_BASE_URL}/processing.php?${queryParams}`);
            if (!response.ok) {
                throw new Error('Failed to fetch processing data');
            }
            const result = await response.json();
            
            if (!result.success) {
                throw new Error(result.error || 'Failed to fetch processing data');
            }

            // Ensure we have an array to work with
            const data = Array.isArray(result.data) ? result.data : [];
            const aggregated = productionService.aggregateProcessing(data, params.groupBy);
            
            // Calculate overall efficiency metrics from raw data
            const efficiency = data.reduce((acc, record) => {
                const feed_tonnage = Number(record.feed_tonnage) || 0;
                const feed_grade = Number(record.feed_grade) || 0;
                const recovery = Number(record.recovery) || 0;
                const produced_gold = Number(record.produced_gold) || 0;
                const tails_grade = Number(record.tails_grade) || 0;

                return {
                    total_feed_tonnage: acc.total_feed_tonnage + feed_tonnage,
                    feed_grade_sum: acc.feed_grade_sum + (feed_grade * feed_tonnage),
                    recovery_sum: acc.recovery_sum + (recovery * feed_tonnage),
                    total_produced_gold: acc.total_produced_gold + produced_gold,
                    tails_grade_sum: acc.tails_grade_sum + (tails_grade * feed_tonnage),
                    total_weight: acc.total_weight + feed_tonnage
                };
            }, {
                total_feed_tonnage: 0,
                feed_grade_sum: 0,
                recovery_sum: 0,
                total_produced_gold: 0,
                tails_grade_sum: 0,
                total_weight: 0
            });

            return {
                monthlyProcessing: aggregated,
                efficiencyMetrics: [{
                    avg_recovery_rate: efficiency.total_weight ? efficiency.recovery_sum / efficiency.total_weight : 0,
                    avg_feed_grade: efficiency.total_weight ? efficiency.feed_grade_sum / efficiency.total_weight : 0,
                    avg_tails_grade: efficiency.total_weight ? efficiency.tails_grade_sum / efficiency.total_weight : 0,
                    gold_per_tonne: efficiency.total_feed_tonnage ? efficiency.total_produced_gold / efficiency.total_feed_tonnage : 0
                }],
                rawData: data
            };
        } catch (error) {
            console.error('Error fetching processing data:', error);
            return {
                monthlyProcessing: [],
                efficiencyMetrics: [{
                    avg_recovery_rate: 0,
                    avg_feed_grade: 0,
                    avg_tails_grade: 0,
                    gold_per_tonne: 0
                }],
                rawData: []
            };
        }
    },

    // Get milling statistics
    getMilling: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                startDate: params.startDate || new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
                endDate: params.endDate || new Date().toISOString().split('T')[0]
            }).toString();

            const response = await fetch(`${API_BASE_URL}/milling.php?${queryParams}`);
            if (!response.ok) {
                throw new Error('Failed to fetch milling data');
            }
            const result = await response.json();
            
            if (!result.success) {
                throw new Error(result.error || 'Failed to fetch milling data');
            }

            return {
                monthlyMilling: productionService.aggregateMilling(result.data, params.groupBy),
                rawData: result.data
            };
        } catch (error) {
            console.error('Error fetching milling data:', error);
            throw error;
        }
    },

    // Format functions for different time periods
    formatPeriod: (period, groupBy = 'month') => {
        switch(groupBy) {
            case 'day':
                return new Date(period).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            case 'week':
                const date = new Date(period);
                return `Week ${Math.ceil((date.getDate() + date.getDay()) / 7)}, ${date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
            case 'month':
                return new Date(period + '-01').toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short'
                });
            case 'quarter':
                const [year, quarter] = period.split('-Q');
                return `Q${quarter} ${year}`;
            case 'year':
                return period;
            default:
                return period;
        }
    },

    // Helper function to format numbers with commas
    formatNumber: (number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(number);
    },

    // Helper function to format currency
    formatCurrency: (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    },

    // Helper function to format percentage
    formatPercentage: (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).format(value / 100);
    }
}; 